import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  FaCertificate,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaClock,
} from 'react-icons/fa';

import api from '../../services/api';

import ActivityIndicator from '../../components/ActivityIndicator';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';

import {
  Badge,
  CardItem,
  Cards,
  Container,
  LoadingContainer,
  PeriodSelect,
  Value,
  Wrapper,
  Section,
  RejeicaoList,
  DetailTable,
  EmptyDetail,
} from './styles';

interface SaudeFiscal {
  emissaoHabilitada: boolean;
  certificado: {
    status: string;
    validade: string | null;
    diasParaExpirar: number | null;
  };
  autorizacao: {
    total: number;
    autorizadas: number;
    percentual: number;
  };
  contingenciasAbertas: number;
}

interface SaudeFiscalDetalhes {
  autorizacao24h: {
    autorizadas: number;
    dentro24h: number;
    percentual: number;
  };
  topRejeicoes: { motivo: string; total: number }[];
  contingencias: {
    chaveAcesso: string | null;
    numero: number | null;
    serie: number | null;
    dataEmissao: string | null;
  }[];
}

const PERIODOS = [
  { label: 'Últimos 7 dias', value: 7 },
  { label: 'Últimos 30 dias', value: 30 },
  { label: 'Últimos 90 dias', value: 90 },
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

// Formata a data a partir dos componentes locais. Usar toISOString() aqui
// converteria para UTC e, para um fuso negativo (ex.: UTC-3), deslocaria a
// fronteira do período em um dia ("últimos 30 dias" viraria 29/31).
function toLocalISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const SaudeFiscal: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(30);
  const [data, setData] = useState<SaudeFiscal | null>(null);
  const [detalhes, setDetalhes] = useState<SaudeFiscalDetalhes | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);

    const fim = new Date();
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - periodo);

    const params = {
      dataInicio: toLocalISODate(inicio),
      dataFim: toLocalISODate(fim),
    };

    try {
      const response = await api.get(
        `companies/${user.empresa_id}/fiscal/saude`,
        { params },
      );

      setData(response.data);

      // Detalhes (T4.8) são best-effort: uma falha aqui não deve esconder os
      // cards principais.
      try {
        const detalhesResponse = await api.get(
          `companies/${user.empresa_id}/fiscal/saude/detalhes`,
          { params },
        );
        setDetalhes(detalhesResponse.data);
      } catch {
        setDetalhes(null);
      }
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar saúde fiscal',
        description: 'Não foi possível buscar os indicadores fiscais.',
      });
    } finally {
      setLoading(false);
    }
  }, [user.empresa_id, periodo, addToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // O backend devolve o código do status; a tela é a única que fala com gente.
  // `nao_verificado` é diferente de `nao_configurado`: o primeiro é "não deu
  // para consultar a emissão agora", o segundo é "não há certificado".
  const CERTIFICADO_LABEL: Record<string, string> = {
    valido: 'Válido',
    expirado: 'Expirado',
    revogado: 'Revogado',
    invalido: 'Inválido',
    expiring: 'Vence em breve',
    nao_configurado: 'Não configurado',
    nao_verificado: 'Não verificado',
  };

  const certificadoStatus = data?.certificado.status ?? '';
  const certificadoLabel =
    CERTIFICADO_LABEL[certificadoStatus] ?? certificadoStatus;
  // Não verificado não é problema do certificado — é falta de informação. Pintar
  // de vermelho mandaria o operador atrás de um defeito que pode não existir.
  const certificadoIndefinido = certificadoStatus === 'nao_verificado';
  const certificadoRuim = ['expirado', 'revogado', 'invalido'].includes(
    certificadoStatus,
  );

  const certificadoVencendo =
    data?.certificado.diasParaExpirar !== null &&
    data?.certificado.diasParaExpirar !== undefined &&
    data.certificado.diasParaExpirar <= 7;

  return (
    <Container>
      <Wrapper>
        <header>
          <div>
            <h1>Saúde Fiscal</h1>
            <h3>Resumo da emissão de NFC-e da sua empresa</h3>
          </div>
          <PeriodSelect>
            <label htmlFor="periodo-saude-fiscal">Período</label>
            <select
              id="periodo-saude-fiscal"
              value={periodo}
              onChange={(e) => setPeriodo(Number(e.target.value))}
            >
              {PERIODOS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </PeriodSelect>
        </header>

        {loading || !data ? (
          <LoadingContainer>
            <ActivityIndicator />
          </LoadingContainer>
        ) : (
          <Cards>
            <CardItem>
              <header>
                <h2>Emissão habilitada</h2>
                <FaFileInvoiceDollar color={theme.colors.main} />
              </header>
              <main>
                {data.emissaoHabilitada ? (
                  <Badge tone="positive">Sim</Badge>
                ) : (
                  <Badge tone="negative">Não</Badge>
                )}
              </main>
              <footer>
                <span>Status da emissão de NFC-e</span>
              </footer>
            </CardItem>

            <CardItem>
              <header>
                <h2>Certificado</h2>
                <FaCertificate
                  color={
                    certificadoIndefinido
                      ? theme.colors.softBlack
                      : certificadoVencendo || certificadoRuim
                      ? theme.colors.red
                      : theme.colors.green
                  }
                />
              </header>
              <main>
                <Value
                  as="h3"
                  tone={
                    !certificadoIndefinido &&
                    (certificadoVencendo || certificadoRuim)
                      ? 'negative'
                      : undefined
                  }
                  style={{ fontSize: 22 }}
                >
                  {certificadoLabel}
                </Value>
                <span style={{ fontSize: 14, color: theme.colors.softBlack }}>
                  Validade: {formatDate(data.certificado.validade)}
                </span>
              </main>
              <footer>
                <span>
                  {certificadoIndefinido
                    ? 'Não foi possível consultar a emissão agora'
                    : data.certificado.diasParaExpirar === null
                    ? 'Sem validade cadastrada'
                    : data.certificado.diasParaExpirar < 0
                    ? `Vencido há ${Math.abs(
                        data.certificado.diasParaExpirar,
                      )} dias`
                    : `Vence em ${data.certificado.diasParaExpirar} dias`}
                </span>
              </footer>
            </CardItem>

            <CardItem>
              <header>
                <h2>NFC-e autorizadas</h2>
                <FaCheckCircle color={theme.colors.green} />
              </header>
              <main>
                <Value
                  tone={
                    data.autorizacao.percentual >= 99
                      ? 'positive'
                      : data.autorizacao.total === 0
                      ? undefined
                      : 'warning'
                  }
                >
                  {data.autorizacao.percentual}%
                </Value>
              </main>
              <footer>
                <span>
                  {data.autorizacao.autorizadas} de {data.autorizacao.total} no
                  período
                </span>
              </footer>
            </CardItem>

            <CardItem>
              <header>
                <h2>Contingências abertas</h2>
                <FaExclamationTriangle
                  color={
                    data.contingenciasAbertas > 0
                      ? theme.colors.orange
                      : theme.colors.green
                  }
                />
              </header>
              <main>
                <Value
                  tone={
                    data.contingenciasAbertas > 0 ? 'warning' : 'positive'
                  }
                >
                  {data.contingenciasAbertas}
                </Value>
              </main>
              <footer>
                <span>Notas em contingência não sincronizadas</span>
              </footer>
            </CardItem>

            {detalhes && (
              <CardItem>
                <header>
                  <h2>Autorizadas em 24h</h2>
                  <FaClock color={theme.colors.main} />
                </header>
                <main>
                  <Value
                    tone={
                      detalhes.autorizacao24h.autorizadas === 0
                        ? undefined
                        : detalhes.autorizacao24h.percentual >= 95
                        ? 'positive'
                        : 'warning'
                    }
                  >
                    {detalhes.autorizacao24h.percentual}%
                  </Value>
                </main>
                <footer>
                  <span>
                    {detalhes.autorizacao24h.dentro24h} de{' '}
                    {detalhes.autorizacao24h.autorizadas} autorizadas dentro do
                    SLA
                  </span>
                </footer>
              </CardItem>
            )}
          </Cards>
        )}

        {!loading && data && detalhes && (
          <>
            <Section>
              <h2>Top 5 rejeições no período</h2>
              {detalhes.topRejeicoes.length === 0 ? (
                <EmptyDetail>Nenhuma rejeição no período. 🎉</EmptyDetail>
              ) : (
                <RejeicaoList>
                  {detalhes.topRejeicoes.map((rej, index) => (
                    <li key={`${rej.motivo}-${index}`}>
                      <span className="motivo" title={rej.motivo}>
                        {rej.motivo}
                      </span>
                      <span className="total">{rej.total}</span>
                    </li>
                  ))}
                </RejeicaoList>
              )}
            </Section>

            <Section>
              <h2>Contingências abertas ({detalhes.contingencias.length})</h2>
              {detalhes.contingencias.length === 0 ? (
                <EmptyDetail>Nenhuma nota em contingência. 🎉</EmptyDetail>
              ) : (
                <DetailTable>
                  <thead>
                    <tr>
                      <th>Nº / Série</th>
                      <th>Chave de acesso</th>
                      <th>Emissão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalhes.contingencias.map((c, index) => (
                      <tr key={`${c.chaveAcesso ?? 'sem-chave'}-${index}`}>
                        <td>
                          {c.numero ?? '—'}
                          {c.serie ? ` / ${c.serie}` : ''}
                        </td>
                        <td className="chave">{c.chaveAcesso ?? '—'}</td>
                        <td>{formatDate(c.dataEmissao)}</td>
                      </tr>
                    ))}
                  </tbody>
                </DetailTable>
              )}
            </Section>
          </>
        )}
      </Wrapper>
    </Container>
  );
};

export default SaudeFiscal;
